import { UseMutationOptions, UseQueryOptions } from "@tanstack/react-query";
import { EmptySlotResponse } from "../library/models/appointment";
import {
    IBookingRequestAccepted,
    IBookingRequestSubmission,
    IPublicTherapist,
    IVerificationPreview,
    IVerifyBookingRequest,
    IVerifyBookingResponse,
} from "../library/models/publicBooking";
import { IService } from "../library/models/service";
import { ITherapistProgram } from "../library/models/therapistProgram";
import { BaseService } from "./baseService";

/**
 * Online booking, for a visitor with no session: `/public/booking/**` on the API.
 *
 * Like `PublicTenantService`, every route answers for the clinic the **hostname** names and
 * none takes a clinic as a parameter, so each read is keyed by hostname -- the URL alone would
 * be one cache entry shared by every clinic, which is harmless in a browser and wrong in a test
 * that renders two. On the platform's own address every route here is a 404.
 */
export class PublicBookingService extends BaseService {

    /** The single sessions a visitor may book: the list `GET /services` answers, signed out. */
    getServices(hostname: string): UseQueryOptions<IService[], Error> {
        return {
            queryKey: ['/public/booking/services', hostname],
            queryFn: (): Promise<IService[]> => this.get<IService[]>('/public/booking/services'),
        };
    }

    getTherapists(hostname: string, serviceId: number): UseQueryOptions<IPublicTherapist[], Error> {
        return {
            queryKey: ['/public/booking/therapists', hostname, serviceId],
            queryFn: (): Promise<IPublicTherapist[]> =>
                this.get<IPublicTherapist[]>('/public/booking/therapists', { params: { serviceId } }),
        };
    }

    getProgram(hostname: string, therapistId: number): UseQueryOptions<ITherapistProgram, Error> {
        return {
            queryKey: ['/public/booking/program', hostname, therapistId],
            queryFn: (): Promise<ITherapistProgram> =>
                this.get<ITherapistProgram>(`/public/booking/therapists/${therapistId}/program`),
        };
    }

    /**
     * Free windows, as a patient is offered them. No window length is sent: the API sizes them
     * from the service itself, so a caller cannot ask for a shorter hour than it sells.
     */
    getSlots(
        hostname: string, serviceId: number, therapistId: number, from: string, to: string
    ): UseQueryOptions<EmptySlotResponse[], Error> {
        return {
            queryKey: ['/public/booking/slots', hostname, serviceId, therapistId, from, to],
            queryFn: (): Promise<EmptySlotResponse[]> =>
                this.get<EmptySlotResponse[]>('/public/booking/slots', {
                    params: { serviceId, therapistId, from, to },
                }),
        };
    }

    submitRequestMutation(): UseMutationOptions<IBookingRequestAccepted, Error, IBookingRequestSubmission> {
        return this.createMutation<IBookingRequestAccepted, IBookingRequestSubmission>('/public/booking/requests');
    }

    /** A POST so the token travels in a body; it changes nothing on the server. */
    previewMutation(): UseMutationOptions<IVerificationPreview, Error, string> {
        return {
            mutationFn: (token: string): Promise<IVerificationPreview> =>
                this.post<IVerificationPreview>('/public/booking/verification/preview', { token }),
        };
    }

    verifyMutation(): UseMutationOptions<IVerifyBookingResponse, Error, IVerifyBookingRequest> {
        return this.createMutation<IVerifyBookingResponse, IVerifyBookingRequest>('/public/booking/verification');
    }
}
